// Estado falso de Tenancy (modo fake, só em dev — Parte 0.6; GIY-046): tenants com pessoa jurídica, sites com localização,
// calendários/feriados/fechamentos, transições de ciclo de vida, bootstrap do administrador, contrato, sobrescritas de
// configuração e registro societário, no formato do contrato (`Tenancy*Response` de schema.d.ts). Semeado por cenário de
// `/me` (os tenants dos contextos PILOT/SECOND + um tenant em `Draft` só visível à rede).
import type { components } from '@giyus/api-client';

import { FIXTURE_IDS, FIXTURE_TENANT_CODES } from '../auth/me.fixture';

type Schemas = components['schemas'];
export type TenantDetailResponse = Schemas['TenancyTenantDetailResponse'];
export type LegalEntityResponse = Schemas['TenancyLegalEntityResponse'];
export type AddressResponse = Schemas['TenancyAddressResponse'];
export type SiteResponse = Schemas['TenancySiteResponse'];
export type TransitionResponse = Schemas['TenancyTenantTransitionResponse'];
export type CalendarVersionResponse = Schemas['TenancyCalendarVersionResponse'];
export type HolidayResponse = Schemas['TenancyHolidayResponse'];
export type ClosureResponse = Schemas['TenancyClosureResponse'];
export type OwnershipRecordResponse = Schemas['TenancyOwnershipRecordResponse'];
export type ContractResponse = Schemas['TenancySharedServicesContractResponse'];
export type BootstrapResponse = Schemas['TenancyTenantAdminBootstrapResponse'];
export type ImpactedAppointment = Schemas['TenancyImpactedAppointmentResponse'];
export type TimeRange = Schemas['TenancyTimeRangeResponse'];

export interface FakeSite extends SiteResponse {
  version: number;
  calendarVersions: CalendarVersionResponse[];
  holidays: HolidayResponse[];
  closures: ClosureResponse[];
  /** Consultas confirmadas do site (SCH chega no M11): alimentam a prévia de impacto e o bloqueio da desativação. */
  appointments: ImpactedAppointment[];
}

export interface FakeLegalEntity extends LegalEntityResponse {
  version: number;
}

export interface FakeContract extends ContractResponse {
  version: number;
}

export interface FakeSettingOverride {
  value: unknown;
  version: number;
}

export interface FakeTenant {
  readonly id: string;
  readonly code: string;
  displayName: string;
  defaultLocale: string;
  status: string;
  admissionPath: string | null;
  conformancePlanDocumentId: string | null;
  conformancePlanDeadline: string | null;
  readonly createdAt: string;
  activatedAt: string | null;
  suspendedAt: string | null;
  offboardedAt: string | null;
  version: number;
  legalEntity: FakeLegalEntity;
  sites: FakeSite[];
  transitions: TransitionResponse[];
  bootstrap: BootstrapResponse | null;
  contract: FakeContract | null;
  settingOverrides: Map<string, FakeSettingOverride>;
  activeUserCount: number;
}

/** Catálogo (Parte 4.9): valor de rede, padrão e se o tenant pode sobrescrever. */
export interface FakeSettingDefinition {
  readonly key: string;
  readonly networkValue: unknown;
  readonly defaultValue: unknown;
  readonly tenantOverridable: boolean;
  readonly requiresTwoKeys: boolean;
}

export interface FakeTenancyState {
  readonly holdingLegalEntityId: string;
  readonly tenants: FakeTenant[];
  readonly ownership: OwnershipRecordResponse[];
  readonly definitions: readonly FakeSettingDefinition[];
  readonly base: string;
  counter: number;
}

export const HOLDING_LEGAL_ENTITY_ID = '01926f3e-0000-7000-8000-0000000000e0';
export const TENANT_DRAFT_ID = '01926f3e-0000-7000-8000-00000000000c';
export const LEGAL_ENTITY_DRAFT_ID = '01926f3e-0000-7000-8000-0000000000e3';

export const DEFAULT_WEEKLY: Record<string, TimeRange[]> = {
  monday: [{ start: '07:00', end: '19:00' }],
  tuesday: [{ start: '07:00', end: '19:00' }],
  wednesday: [{ start: '07:00', end: '19:00' }],
  thursday: [{ start: '07:00', end: '19:00' }],
  friday: [{ start: '07:00', end: '19:00' }],
  saturday: [],
  sunday: [],
};

export function fakeTenancyId(state: { counter: number }): string {
  state.counter += 1;
  return `01926f3e-0000-7000-8000-00000000a${state.counter.toString(16).padStart(3, '0')}`;
}

export function shiftIso(base: string, minutes: number): string {
  return new Date(new Date(base).getTime() + minutes * 60_000).toISOString();
}

/** Data civil (`yyyy-MM-dd`) de um instante num fuso IANA. */
export function civilDate(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function addDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days))
    .toISOString()
    .slice(0, 10);
}

function address(
  partial: Partial<AddressResponse> & Pick<AddressResponse, 'city' | 'state'>,
): AddressResponse {
  return {
    street: partial.street ?? 'Rua das Pesquisas',
    number: partial.number ?? '100',
    complement: partial.complement ?? null,
    district: partial.district ?? 'Centro',
    city: partial.city,
    state: partial.state,
    postalCode: partial.postalCode ?? '80000-000',
    country: 'BR',
  };
}

export function formatCnpj(digits: string): string {
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

function legalEntity(
  id: string,
  legalName: string,
  cnpj: string,
  city: string,
  state: string,
  representative: string,
): FakeLegalEntity {
  return {
    id,
    kind: 'Center',
    legalName,
    tradeName: null,
    country: 'BR',
    registrationNumber: cnpj,
    registrationNumberFormatted: formatCnpj(cnpj),
    stateRegistration: null,
    municipalRegistration: null,
    taxRegime: 'LucroReal',
    taxRegimeExceptionApprovalId: null,
    legalRepresentative: {
      name: representative,
      cpfMasked: '***.***.***-00',
      email: `${representative.split(' ')[0]?.toLowerCase() ?? 'contato'}@centro.example`,
    },
    institutionalEmail: 'contato@centro.example',
    institutionalPhone: '+55 41 3333-0000',
    address: address({ city, state }),
    version: 1,
  };
}

function site(
  base: string,
  partial: Pick<FakeSite, 'id' | 'tenantId' | 'code' | 'name' | 'timeZoneId'> & {
    city: string;
    state: string;
    ibge: string | null;
    latitude: number | null;
    longitude: number | null;
    appointments?: ImpactedAppointment[];
    status?: string;
  },
): FakeSite {
  const hasCoordinates = partial.latitude !== null && partial.longitude !== null;
  return {
    id: partial.id,
    tenantId: partial.tenantId,
    code: partial.code,
    name: partial.name,
    timeZoneId: partial.timeZoneId,
    phone: null,
    status: partial.status ?? 'Active',
    address: address({ city: partial.city, state: partial.state }),
    state: partial.state,
    ibgeMunicipalityCode: partial.ibge,
    regionFreeText: null,
    latitude: partial.latitude,
    longitude: partial.longitude,
    locationSource: hasCoordinates ? 'Manual' : 'Address',
    version: 1,
    calendarVersions: [
      {
        id: `${partial.id.slice(0, -2)}c1`,
        siteId: partial.id,
        effectiveFrom: '2026-01-01',
        effectiveTo: null,
        weekly: structuredClone(DEFAULT_WEEKLY),
        note: 'Horário padrão',
        createdAt: shiftIso(base, -60 * 24 * 200),
        createdBy: null,
      },
    ],
    holidays: [],
    closures: [],
    appointments: partial.appointments ?? [],
  };
}

function transition(
  state: { counter: number },
  tenantId: string,
  from: string,
  to: string,
  decidedAt: string,
  tenantStatus: string,
): TransitionResponse {
  return {
    id: fakeTenancyId(state),
    tenantId,
    fromStatus: from,
    toStatus: to,
    reasonCode: 'PREREQUISITES_MET',
    reasonText: null,
    status: 'Applied',
    requiresTwoKeys: false,
    twoKeyApprovalId: null,
    decidedBy: '01926f3e-0000-7000-8000-0000000000u3',
    decidedAt,
    secondKeyBy: null,
    secondKeyAt: null,
    appliedAt: decidedAt,
    tenantStatus,
  };
}

function bootstrap(
  state: { counter: number },
  tenantId: string,
  status: 'Pending' | 'Active',
  createdAt: string,
): BootstrapResponse {
  return {
    id: fakeTenancyId(state),
    tenantId,
    personId: fakeTenancyId(state),
    userId: fakeTenancyId(state),
    invitationId: status === 'Pending' ? fakeTenancyId(state) : null,
    roleCode: 'TENANT_ADMIN',
    status,
    createdAt,
    activatedAt: status === 'Active' ? shiftIso(createdAt, 60 * 24) : null,
  };
}

/** O seed não depende do cenário de `/me`: a visibilidade de cada tenant é decidida pelas rotas (contextos ou rede). */
export function seedTenancyState(): FakeTenancyState {
  const base = new Date().toISOString();
  const counter = { counter: 0 };
  const createdPilot = shiftIso(base, -60 * 24 * 220);
  const createdSecond = shiftIso(base, -60 * 24 * 40);
  const createdDraft = shiftIso(base, -60 * 24 * 3);
  const sedeTz = 'America/Sao_Paulo';
  const nextVisitDay = addDays(civilDate(base, sedeTz), 3);

  const pilot: FakeTenant = {
    id: FIXTURE_IDS.tenantPilot,
    code: FIXTURE_TENANT_CODES.pilot,
    displayName: 'Centro-âncora (piloto)',
    defaultLocale: 'pt-BR',
    status: 'Active',
    admissionPath: 'AdmissaoPlena',
    conformancePlanDocumentId: null,
    conformancePlanDeadline: null,
    createdAt: createdPilot,
    activatedAt: shiftIso(createdPilot, 60 * 24 * 30),
    suspendedAt: null,
    offboardedAt: null,
    version: 1,
    legalEntity: legalEntity(
      FIXTURE_IDS.legalEntityPilot,
      'Centro de Pesquisa Piloto Ltda.',
      '45723174000110',
      'Curitiba',
      'PR',
      'Helena Prado',
    ),
    sites: [
      site(base, {
        id: FIXTURE_IDS.sitePilotSede,
        tenantId: FIXTURE_IDS.tenantPilot,
        code: 'SEDE',
        name: 'Sede',
        timeZoneId: sedeTz,
        city: 'Curitiba',
        state: 'PR',
        ibge: '4106902',
        latitude: -25.4284,
        longitude: -49.2733,
        appointments: [
          {
            appointmentId: '01926f3e-0000-7000-8000-000000000a01',
            studySiteId: '01926f3e-0000-7000-8000-000000005501',
            scheduledAt: new Date(`${nextVisitDay}T13:00:00Z`).toISOString(),
            status: 'Confirmed',
            ownerUserId: '01926f3e-0000-7000-8000-0000000000u1',
          },
          {
            appointmentId: '01926f3e-0000-7000-8000-000000000a02',
            studySiteId: '01926f3e-0000-7000-8000-000000005501',
            scheduledAt: new Date(`${nextVisitDay}T17:00:00Z`).toISOString(),
            status: 'Confirmed',
            ownerUserId: '01926f3e-0000-7000-8000-0000000000u1',
          },
        ],
      }),
      site(base, {
        id: FIXTURE_IDS.sitePilotAnexo,
        tenantId: FIXTURE_IDS.tenantPilot,
        code: 'ANEXO',
        name: 'Anexo Manaus',
        timeZoneId: 'America/Manaus',
        city: 'Manaus',
        state: 'AM',
        ibge: '1302603',
        latitude: null,
        longitude: null,
      }),
    ],
    transitions: [],
    bootstrap: bootstrap(counter, FIXTURE_IDS.tenantPilot, 'Active', createdPilot),
    contract: {
      id: fakeTenancyId(counter),
      tenantId: FIXTURE_IDS.tenantPilot,
      documentId: '01926f3e-0000-7000-8000-00000000dc01',
      effectiveFrom: '2026-03-01',
      effectiveTo: null,
      monthlyMinimumAmount: 12000,
      feeRuleDescription:
        'Percentual sobre a receita de estudos ativos, conforme anexo II do contrato.',
      status: 'Active',
      modifiedAt: shiftIso(createdPilot, 60 * 24 * 20),
      version: 1,
    },
    settingOverrides: new Map(),
    activeUserCount: 12,
  };
  pilot.transitions = [
    transition(
      counter,
      pilot.id,
      'Draft',
      'Onboarding',
      shiftIso(createdPilot, 60 * 24 * 5),
      'Active',
    ),
    transition(
      counter,
      pilot.id,
      'Onboarding',
      'Active',
      pilot.activatedAt ?? createdPilot,
      'Active',
    ),
  ];
  const sede = pilot.sites[0];
  if (sede) {
    sede.holidays.push({
      id: fakeTenancyId(counter),
      siteId: sede.id,
      scope: 'Site',
      state: null,
      ibgeMunicipalityCode: null,
      date: `${civilDate(base, sedeTz).slice(0, 4)}-10-20`,
      year: Number(civilDate(base, sedeTz).slice(0, 4)),
      name: 'Aniversário do centro',
      closesSite: true,
      source: 'Manual',
    });
    sede.closures.push({
      id: fakeTenancyId(counter),
      siteId: sede.id,
      startDate: addDays(civilDate(base, sedeTz), 10),
      endDate: addDays(civilDate(base, sedeTz), 10),
      startTime: null,
      endTime: null,
      reason: 'Manutenção elétrica programada',
      category: 'Maintenance',
      retroactiveJustification: null,
      createdAt: shiftIso(base, -60 * 24 * 2),
      createdBy: null,
    });
  }

  const second: FakeTenant = {
    id: FIXTURE_IDS.tenantSecond,
    code: FIXTURE_TENANT_CODES.second,
    displayName: 'Centro parceiro',
    defaultLocale: 'pt-BR',
    status: 'Onboarding',
    admissionPath: 'AdmissaoPlena',
    conformancePlanDocumentId: null,
    conformancePlanDeadline: null,
    createdAt: createdSecond,
    activatedAt: null,
    suspendedAt: null,
    offboardedAt: null,
    version: 1,
    legalEntity: legalEntity(
      FIXTURE_IDS.legalEntitySecond,
      'Instituto Parceiro de Pesquisa Clínica Ltda.',
      '11222333000181',
      'São Paulo',
      'SP',
      'Rafael Moura',
    ),
    sites: [
      site(base, {
        id: FIXTURE_IDS.siteSecondUnidade,
        tenantId: FIXTURE_IDS.tenantSecond,
        code: 'UNIDADE-1',
        name: 'Unidade 1',
        timeZoneId: 'America/Sao_Paulo',
        city: 'São Paulo',
        state: 'SP',
        ibge: '3550308',
        latitude: -23.5505,
        longitude: -46.6333,
      }),
    ],
    transitions: [],
    bootstrap: bootstrap(counter, FIXTURE_IDS.tenantSecond, 'Active', createdSecond),
    contract: null,
    settingOverrides: new Map(),
    activeUserCount: 4,
  };
  second.transitions = [
    transition(
      counter,
      second.id,
      'Draft',
      'Onboarding',
      shiftIso(createdSecond, 60 * 24 * 2),
      'Onboarding',
    ),
  ];

  const draft: FakeTenant = {
    id: TENANT_DRAFT_ID,
    code: 'NOVO',
    displayName: 'Centro novo (em cadastro)',
    defaultLocale: 'pt-BR',
    status: 'Draft',
    admissionPath: null,
    conformancePlanDocumentId: null,
    conformancePlanDeadline: null,
    createdAt: createdDraft,
    activatedAt: null,
    suspendedAt: null,
    offboardedAt: null,
    version: 1,
    legalEntity: legalEntity(
      LEGAL_ENTITY_DRAFT_ID,
      'Clínica Nova de Pesquisa S.A.',
      '99888777000166',
      'Belo Horizonte',
      'MG',
      'Tânia Ribeiro',
    ),
    sites: [],
    transitions: [],
    bootstrap: bootstrap(counter, TENANT_DRAFT_ID, 'Pending', createdDraft),
    contract: null,
    settingOverrides: new Map(),
    activeUserCount: 0,
  };

  const ownership: OwnershipRecordResponse[] = [
    {
      id: fakeTenancyId(counter),
      ownedLegalEntityId: FIXTURE_IDS.legalEntityPilot,
      holderKind: 'LegalEntity',
      holderPersonId: null,
      holderLegalEntityId: HOLDING_LEGAL_ENTITY_ID,
      holderType: 'Holding',
      percentage: 60,
      effectiveFrom: '2026-03-01',
      effectiveTo: null,
      referenceDocumentId: null,
      note: null,
      status: 'Active',
      createdAt: shiftIso(createdPilot, 60 * 24 * 10),
      createdBy: null,
    },
    {
      id: fakeTenancyId(counter),
      ownedLegalEntityId: FIXTURE_IDS.legalEntityPilot,
      holderKind: 'Person',
      holderPersonId: '01926f3e-0000-7000-8000-0000000000f9',
      holderLegalEntityId: null,
      holderType: 'InvestigatorPartner',
      percentage: 40,
      effectiveFrom: '2026-03-01',
      effectiveTo: null,
      referenceDocumentId: null,
      note: 'Investigadora principal fundadora',
      status: 'Active',
      createdAt: shiftIso(createdPilot, 60 * 24 * 10),
      createdBy: null,
    },
  ];

  const definitions: FakeSettingDefinition[] = [
    {
      key: 'tenant.defaultLocale',
      networkValue: 'pt-BR',
      defaultValue: 'pt-BR',
      tenantOverridable: true,
      requiresTwoKeys: false,
    },
    {
      key: 'tenant.calendar.defaultWeekly',
      networkValue: structuredClone(DEFAULT_WEEKLY),
      defaultValue: structuredClone(DEFAULT_WEEKLY),
      tenantOverridable: true,
      requiresTwoKeys: false,
    },
    {
      key: 'tenant.lifecycle.requireTwoKeysForActivation',
      networkValue: false,
      defaultValue: false,
      tenantOverridable: false,
      requiresTwoKeys: false,
    },
    {
      key: 'tenant.export.sasMinutes',
      networkValue: 15,
      defaultValue: 15,
      tenantOverridable: false,
      requiresTwoKeys: false,
    },
    {
      key: 'tenant.closure.retroactiveDays',
      networkValue: 0,
      defaultValue: 0,
      tenantOverridable: false,
      requiresTwoKeys: false,
    },
    {
      key: 'tenant.site.geocoding.enabled',
      networkValue: false,
      defaultValue: false,
      tenantOverridable: false,
      requiresTwoKeys: false,
    },
  ];

  // Os tenants dos contextos do usuário sempre existem no seed; o `Draft` só é alcançável pela rede.
  return {
    holdingLegalEntityId: HOLDING_LEGAL_ENTITY_ID,
    tenants: [pilot, second, draft],
    ownership,
    definitions,
    base,
    counter: counter.counter,
  };
}

export function toSiteResponse(entry: FakeSite): SiteResponse {
  return {
    id: entry.id,
    tenantId: entry.tenantId,
    code: entry.code,
    name: entry.name,
    timeZoneId: entry.timeZoneId,
    phone: entry.phone,
    status: entry.status,
    address: entry.address,
    state: entry.state,
    ibgeMunicipalityCode: entry.ibgeMunicipalityCode,
    regionFreeText: entry.regionFreeText,
    latitude: entry.latitude,
    longitude: entry.longitude,
    locationSource: entry.locationSource,
  };
}

export function toLegalEntityResponse(entry: FakeLegalEntity): LegalEntityResponse {
  return {
    id: entry.id,
    kind: entry.kind,
    legalName: entry.legalName,
    tradeName: entry.tradeName,
    country: entry.country,
    registrationNumber: entry.registrationNumber,
    registrationNumberFormatted: entry.registrationNumberFormatted,
    stateRegistration: entry.stateRegistration,
    municipalRegistration: entry.municipalRegistration,
    taxRegime: entry.taxRegime,
    taxRegimeExceptionApprovalId: entry.taxRegimeExceptionApprovalId,
    legalRepresentative: entry.legalRepresentative,
    institutionalEmail: entry.institutionalEmail,
    institutionalPhone: entry.institutionalPhone,
    address: entry.address,
  };
}

export function toContractResponse(entry: FakeContract): ContractResponse {
  return {
    id: entry.id,
    tenantId: entry.tenantId,
    documentId: entry.documentId,
    effectiveFrom: entry.effectiveFrom,
    effectiveTo: entry.effectiveTo,
    monthlyMinimumAmount: entry.monthlyMinimumAmount,
    feeRuleDescription: entry.feeRuleDescription,
    status: entry.status,
    modifiedAt: entry.modifiedAt,
  };
}

export function toTenantDetail(tenant: FakeTenant): TenantDetailResponse {
  return {
    id: tenant.id,
    code: tenant.code,
    displayName: tenant.displayName,
    defaultLocale: tenant.defaultLocale,
    status: tenant.status,
    admissionPath: tenant.admissionPath,
    conformancePlanDocumentId: tenant.conformancePlanDocumentId,
    conformancePlanDeadline: tenant.conformancePlanDeadline,
    createdAt: tenant.createdAt,
    activatedAt: tenant.activatedAt,
    suspendedAt: tenant.suspendedAt,
    offboardedAt: tenant.offboardedAt,
    legalEntity: toLegalEntityResponse(tenant.legalEntity),
    sites: tenant.sites.map(toSiteResponse),
    adminBootstrap: tenant.bootstrap,
  };
}

export function etagOf(version: number, prefix = ''): string {
  return `W/"${prefix}${String(version)}"`;
}
